import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  Input,
  TemplateRef,
} from '@angular/core';
import { capitalize } from 'lodash-es';
import { BehaviorSubject, map, switchMap, tap } from 'rxjs';

import { LetDirectiveModule } from '@plugins/client/directives/let-directive.module';
import { NoticeComponent } from '@plugins/client/notice';
import { DompurifyPipe } from '@plugins/client/pipes/html/dompurify.pipe';
import { TranslatePipe } from '@plugins/client/translate/pipes';
import { MiddlewareSdkModule } from '@plugins/middleware-sdk';
import { SUPPORT_EMAIL } from '@plugins/shared/constants';
import { Nullish, SingleOrArray } from '@plugins/shared/types';
import { PlatformFeatures } from '@plugins/shared/types/auth';
import { returnAsArray } from '@plugins/shared/utils/arrayUtils/array-utils';

import { FeatureCheckTranslationModule } from '../translation-loader';

import { FeatureCheckService } from './feature-check.service';

type PlatformKey = keyof PlatformFeatures;
type CustomWarning = string | undefined;
export type FeatureCheckInputs = {
  warningType: 'frontend' | 'backoffice';
  disabledTemplate?: TemplateRef<unknown>;
  customWarning?: string;
  divider?: boolean;
  feature: SingleOrArray<PlatformKey> | undefined;
  hideIfDisabled?: boolean;
};

@Component({
  selector: 'plugins-feature-check',
  imports: [
    CommonModule,
    FeatureCheckTranslationModule,
    TranslatePipe,
    MiddlewareSdkModule,
    NoticeComponent,
    DompurifyPipe,
    LetDirectiveModule,
  ],
  templateUrl: './feature-check.component.html',
  styleUrl: './feature-check.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatureCheckComponent {
  readonly warningType = input<FeatureCheckInputs['warningType']>('frontend');
  /** Template that will be shown if feature is disabled.
   * Takes precedence over the `customWarning` input. */
  readonly disabledTemplate = input(null, {
    transform: (val: Nullish | TemplateRef<unknown>) => val ?? null,
  });
  /** Custom warning to be displayed if any of the
   * checked features are disabled. The warning will be
   * sanitized with the `dompurify` pipe. */
  readonly customWarning = input<CustomWarning, CustomWarning>(undefined, {
    transform: (val: FeatureCheckInputs['customWarning']) =>
      val?.length ? val : undefined,
  });

  /** Hide any warnings and projected content if feature is
   * disabled. */
  readonly hideIfDisabled = input<FeatureCheckInputs['hideIfDisabled']>(false);
  readonly divider = input<FeatureCheckInputs['divider'] | Nullish>();
  /** Provide one or several platform features that need to be enbled
   * in order for the projected content to work properly.
   *
   * If no features are supplied, content will automatically be
   * shown */
  // ? Keeping deprecated setter until RxJs interop is stable
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set feature(features: FeatureCheckInputs['feature']) {
    this.#features.next(features ? returnAsArray(features) : []);
  }

  protected featureService = inject(FeatureCheckService);
  protected elementRef = inject(ElementRef) as ElementRef<HTMLElement>;

  #isFeatureEnabled = new BehaviorSubject(false);
  isFeatureEnabled$ = this.#isFeatureEnabled.asObservable();
  get isFeatureEnabled() {
    return this.#isFeatureEnabled.getValue();
  }

  #features = new BehaviorSubject<SingleOrArray<PlatformKey>>([]);
  protected features$ = this.#features.asObservable().pipe(
    switchMap((features) =>
      this.featureService.featuresAvailability(features).pipe(
        tap(({ unavailable }) => {
          this.logUnavailableOrDisabled('unavailable', unavailable);
          // Host element display set to none to avoid possible
          // layout effects (such as flex gaps) in parent elements
          this.elementRef.nativeElement.style.display = unavailable.length
            ? 'none'
            : 'block';
        }),
        map((availability) => ({ availability, features })),
      ),
    ),
    switchMap(({ availability, features }) =>
      this.featureService.featuresEnabled(features).pipe(
        tap(({ disabled, unavailable }) => {
          // Log warning for features that are available, but disabled
          const disabledFeatures = disabled.filter(
            (feature) => !unavailable.includes(feature),
          );
          this.logUnavailableOrDisabled('disabled', disabledFeatures);
          this.#isFeatureEnabled.next(!disabled.length);
        }),
        tap(({ disabled }) => {
          if (disabled.length && this.hideIfDisabled()) {
            this.elementRef.nativeElement.style.display = 'none';
          }
        }),
        map(({ disabled }) => {
          return {
            isEnabled: !disabled.length,
            isAvailable: !availability.unavailable.length,
            disabledMessageArgs: {
              features: disabled.join(', '),
              email: SUPPORT_EMAIL,
            },
            canShow:
              !availability.unavailable.length &&
              (!disabled.length || !this.hideIfDisabled()),
          };
        }),
      ),
    ),
  );

  protected logUnavailableOrDisabled(
    type: 'unavailable' | 'disabled',
    features: string[],
  ) {
    if (!features.length) return;
    console.warn(`${capitalize(type)} feature(s): ${features.join(', ')}.`);
  }

  protected readonly disabledTranslationKey = computed(
    () => `featureCheck.notEnabled.${this.warningType()}`,
  );
}
